// Browser side of an admin <-> member / provider video call (WebRTC). The server only rings and passes these messages along.

/** One call's connection. isCaller makes the offer; the other side answers it. */
export const createPeer = ({ socket, callId, iceServers, localStream, isCaller, onRemoteStream, onState }) => {
    const pc = new RTCPeerConnection({ iceServers });
    localStream.getTracks().forEach(track => pc.addTrack(track, localStream));
    const remote = new MediaStream();
    pc.ontrack = (event) => {
        (event.streams[0] ? event.streams[0].getTracks() : [event.track]).forEach(track => { if (!remote.getTracks().includes(track)) remote.addTrack(track); });
        onRemoteStream?.(remote);
    };
    pc.onicecandidate = (event) => {
        if (event.candidate) socket.emit('call:signal', { callId, data: { candidate: event.candidate } });
    };
    pc.onconnectionstatechange = () => onState?.(pc.connectionState);

    let remoteReady = false;
    let pending = [];
    const apply = async (data) => {
        if (!data) return;
        if (data.sdp) {
            await pc.setRemoteDescription(data.sdp);
            remoteReady = true;
            for (const candidate of pending) await pc.addIceCandidate(candidate).catch(() => { });
            pending = [];
            if (data.sdp.type === 'offer') {
                const answer = await pc.createAnswer();
                await pc.setLocalDescription(answer);
                socket.emit('call:signal', { callId, data: { sdp: pc.localDescription } });
            }
        } else if (data.candidate) {
            if (remoteReady) await pc.addIceCandidate(data.candidate).catch(() => { });
            else pending.push(data.candidate);
        }
    };
    const start = async () => {
        if (!isCaller) return;
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        socket.emit('call:signal', { callId, data: { sdp: pc.localDescription } });
    };
    const close = () => { try { pc.close(); } catch { /* already closed */ } };
    return { pc, apply, start, close };
};

/** Camera + microphone; throws a friendly message if the person says no or there is no camera. */
export const getMedia = async () => {
    if (!navigator.mediaDevices?.getUserMedia) throw new Error('This browser cannot make video calls. Use a recent Chrome, Edge, Firefox or Safari over https.');
    try {
        return await navigator.mediaDevices.getUserMedia({ video: { width: { ideal: 1280 }, height: { ideal: 720 } }, audio: true });
    } catch (error) {
        if (error?.name === 'NotAllowedError') throw new Error('Allow the camera and microphone in the browser to take the call.');
        if (error?.name === 'NotFoundError') throw new Error('No camera or microphone was found on this device.');
        throw new Error('Could not start the camera and microphone.');
    }
};

/** A phone-like ring tone made in the browser (no sound file). Returns a function that stops it. */
export const startRing = () => {
    let stopped = false;
    let ctx = null;
    try { ctx = new (window.AudioContext || window.webkitAudioContext)(); } catch { return () => { }; }
    const beep = (freq, when, length) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.0001, ctx.currentTime + when);
        gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + when + 0.03);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + when + length);
        osc.connect(gain); gain.connect(ctx.destination);
        osc.start(ctx.currentTime + when); osc.stop(ctx.currentTime + when + length + 0.05);
    };
    const ring = () => { if (stopped) return; beep(880, 0, 0.35); beep(660, 0.45, 0.35); };
    ring();
    const timer = setInterval(ring, 2200);
    return () => { stopped = true; clearInterval(timer); try { ctx.close(); } catch { /* ignore */ } };
};
